import {
  Component,
  EventEmitter,
  OnInit,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { DxListComponent } from 'devextreme-angular';
import { OrganisationUnitTypeModel } from 'src/app/models/organisation-unit/organisation-unit-type.model';
import { OrganisationUnitModel } from 'src/app/models/organisation-unit/organisation-unit.model';
import { PersonSummaryModel } from 'src/app/models/person/person-summary.model';
import { PersonModel } from 'src/app/models/person/person.model';
import { TrainingInvitationSearchModel } from 'src/app/models/training/training-invitation-search.model';
import { LoadingService } from 'src/app/services/loading.service';
import { OrganisationUnitService } from 'src/app/services/organisation-unit.service';
import { TrainingMessageService } from 'src/app/services/training-message.service';
import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';
import { TrainingGroupModel } from 'src/app/models/training-group.model';
import { TrainingGroupService } from 'src/app/services/training-group.service';
import { forkJoin, shareReplay } from 'rxjs';

@Component({
  selector: 'app-select-additional-participants-modal',
  standalone: false,
  templateUrl: './select-additional-participants-modal.component.html',
  styleUrl: './select-additional-participants-modal.component.scss'
})
export class SelectAdditionalParticipantsModalComponent implements OnInit {
  @ViewChild('list', { static: true })
  list!: DxListComponent;

  @Input() showModal = false;
  @Output() showModalChange = new EventEmitter<boolean>();

  @Output() onAdd = new EventEmitter<PersonModel[]>();
  @Output() onCancel = new EventEmitter();

  @Input() excludePersonIds: string[] = [];

  organisationUnits: OrganisationUnitModel[] = [];
  trainingGroups: TrainingGroupModel[] = [];

  allPersons: PersonModel[] = [];
  selectedPersons: PersonModel[] = [];
  page: number = 0;

  isAllSelected: boolean | null | undefined = false;
  isSearchLimit: boolean = false;

  formData = {
    organisationUnitFilter: [] as string[],
    trainingGroupFilter: [] as string[],
    search: '',
  };

  store!: CustomStore;
  dataSource!: DataSource;

  constructor(
    private organisationUnitService: OrganisationUnitService,
    private trainingInvitationService: TrainingMessageService,
    private loadingService: LoadingService,
    private trainingGroupService: TrainingGroupService
  ) {}

 
  ngOnInit(): void {
    const formData = this.loadingService
      .load(
        forkJoin({
          trainingGroups: this.trainingGroupService.getAllTrainingGroups(),
          organisationUnits:
            this.organisationUnitService.getAllOrganisationUnits(),
        })
      )
      .pipe(shareReplay(1));

    formData.subscribe(
      ({
        trainingGroups,
        organisationUnits,
        
      }) => {
        this.trainingGroups = trainingGroups;
        this.organisationUnits = organisationUnits;
        this.doSearch(true);
      });
  }

  get isAddDisabled(): boolean {
    return this.selectedPersons.length === 0;
  }

  onOrganizationUnitsSelected(e: any) {
    const currentSelectionId = e.itemData.id;
    const isSelection = e.node.selected;

    const currentUnit = this.organisationUnits.find(
      (unit) => unit.id === currentSelectionId
    );

    if (currentUnit) {
      currentUnit.selected = isSelection;
    }

    const setChildrenSelection = (parentId: string, selected: boolean) => {
      this.organisationUnits.forEach((unit) => {
        if (unit.parentId === parentId) {
          unit.selected = selected;
          setChildrenSelection(unit.id, selected);
        }
      });
    };

    setChildrenSelection(currentSelectionId, isSelection);

    const checkAndDeselectParents = (childParentId: string | null) => {
      if (!childParentId) return;

      const siblings = this.organisationUnits.filter(
        (unit) => unit.parentId === childParentId
      );
      const allSelected = siblings.every((s) => s.selected);

      if (!allSelected) {
        const parentUnit = this.organisationUnits.find(
          (unit) => unit.id === childParentId
        );
        if (parentUnit && parentUnit.selected) {
          parentUnit.selected = false;
          checkAndDeselectParents(parentUnit.parentId);
        }
      }
    };

    if (!isSelection) {
      checkAndDeselectParents(currentUnit?.parentId ?? null);
    }

    const selectedOrganisationUnitsIds = this.organisationUnits
      .filter((unit) => unit.selected)
      .map((unit) => unit.id);
    this.formData.organisationUnitFilter = selectedOrganisationUnitsIds;
    
    this.organisationUnits = [...this.organisationUnits];
    this.doSearch(true);
  }

  onTrainingGroupSelected(e: any) {
    if (e.node.selected) {
      this.formData.trainingGroupFilter = [
        ...this.formData.trainingGroupFilter,
        e.node.key,
      ];
    } else {
      this.formData.trainingGroupFilter =
        this.formData.trainingGroupFilter.filter((id) => id !== e.node.key);
    }

    this.doSearch(true);
  }


  onSerachValueChanged(e: any) {
    this.doSearch(true);
  }

  doSearch(resetSearch: boolean, allPersonsSelected: boolean = false): void {
    if (!resetSearch && this.isSearchLimit) {
      return;
    } else if (resetSearch) {
      this.isSearchLimit = false;
    }

    const page = resetSearch ? 0 : this.page + 1;
    const model: TrainingInvitationSearchModel = {
      page,
      searchText: this.formData.search,
      organisationUnitIdFilter: this.formData.organisationUnitFilter,
      trainingGroupIdFilter: this.formData.trainingGroupFilter,
      excludePersonIds: this.excludePersonIds,
      allPersonsSelected
    };

    this.loadingService
      .load(this.trainingInvitationService.searchPersonsForInvitation(model))
      .subscribe((result) => {
        if (!result.persons.length) {
          this.isSearchLimit = true;
        }

        this.page = result.page;
        const data = result.persons.map((person: PersonModel) => {
          person.selected = allPersonsSelected ? true : false;
          return person;
        });
        
        this.allPersons = resetSearch
          ? data
          : this.allPersons.concat(data);

        if (allPersonsSelected) {
          this.isAllSelected = true;
          this.selectedPersons = [...this.allPersons];
        } else {
          this.setAlreadySelected();
          this.checkIfAllSelected();
        }
      });
  }

  onScrolled(event: any) {
    const scrollTop = event.target.scrollTop;
    const scrollHeight = event.target.scrollHeight;
    const offsetHeight = event.target.offsetHeight;

    if (scrollTop + offsetHeight >= scrollHeight) {
      this.doSearch(false);
    }
  }

  setAlreadySelected() {
    this.allPersons.forEach((person: PersonModel) => {
      const existingPerson = this.selectedPersons.find((selectedPerson: PersonModel) => selectedPerson.id == person.id);

      if (existingPerson) {
        person.selected = true;
      }
    })
  }

  onPersonSelected(e: any, person: PersonModel) {
    if (person.selected) {
      this.selectedPersons.push(person);
    } else {
      this.selectedPersons = this.selectedPersons.filter(
        (selected: PersonModel) => selected.id !== person.id
      );
    }

    this.checkIfAllSelected();
  }

  checkIfAllSelected() {
    const isAnyUnselected = !!this.allPersons.find((person: PersonModel) => !person.selected);
    const isAllSelected = !this.allPersons.find((person: PersonModel) => !person.selected);

    if (this.isAllSelected && isAnyUnselected) {
      this.isAllSelected = false;
    } else if (isAllSelected) {
      this.isAllSelected = true;
    }
  }

  onAllEmployeesClick(e: any) {
    if (!this.isAllSelected) {
      this.doSearch(true, true);
    } else {
      this.allPersons.forEach((person: PersonModel) => {
        person.selected = false;
      })
    }
  }

  onRemoveClicked(person?: PersonModel | PersonSummaryModel) {
    if (!person) {
      this.allPersons.forEach((person: PersonModel) => person.selected = false)
      return;
    }
    const index = this.selectedPersons.map((p) => p.id).indexOf(person.id);
    if (index < 0) {
      return;
    }
    this.selectedPersons.splice(index, 1);
  }


  onScroll(event: any) {
    const element = event.target;
    // Check if the user has scrolled to the bottom
    if (element.scrollHeight - element.scrollTop <= element.clientHeight + 1) {
      this.doSearch(false)
    }
  }

  onAddClick = () => {
    this.onAdd.emit(this.selectedPersons);
    this.toggleShowModal();
  };

  onCancelClick = () => {
    this.onCancel.emit();
    this.toggleShowModal();
  };

  toggleShowModal() {
    this.showModal = !this.showModal;
    this.showModalChange.emit(this.showModal);
  }
}
